import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { requirePremium } from '../../lib/premium';
import { usePremium, useSession } from '../../lib/session';
import { notesWithoutFlats } from '../../utils/Dictionaries/brassNotesWithoutFlats';
import { getDefaultType, getTypes } from '../../utils/Dictionaries/instrumentTypes';
import { keySignatures } from '../../utils/Dictionaries/keySignatures';
import { scalesDict } from '../../utils/Dictionaries/scalesDict';
import {
  BB_PART_NOTATION,
  NOTATIONS,
  displayedTonic,
  normalizeTrumpetKey,
  parseNote,
  resolveNotation,
  scaleToDisplayed,
  writtenToDisplayed,
} from '../../utils/transposition';
import Styles from './Options.module.css';

const PREMIUM_REASON = 'Key signatures and custom ranges are Premium features.';

const INSTRUMENTS = [
  { value: 'trumpet', label: 'Trumpet' },
  { value: 'frenchHorn', label: 'French Horn' },
  { value: 'trombone', label: 'Trombone' },
  { value: 'euphonium', label: 'Euphonium' },
  { value: 'tuba', label: 'Tuba' },
];

const RANGES = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'custom', label: 'Custom' },
];

const loadFromLocalStorage = () => {
  try {
    const storedData = JSON.parse(localStorage.getItem('options'));
    if (storedData) return storedData;
  } catch (e) {
    // fall through to defaults
  }
  return {
    selectedInstrument: 'trumpet',
    selectedRange: 'advanced',
    slider1Value: 0,
    slider2Value: notesWithoutFlats['trumpet'].length - 1,
    keyLetter: 'none',
    accidental: 'natural',
    mode: 'major',
    volume: 50,
    instrumentType: 'Bb',
    notation: 'written',
    euphoniumTrigger: false,
    scaleKey: 'C',
    scaleOctave: 4,
  };
};

// Metronome settings for the Scales page. Kept under their own key; the Scales
// page re-reads them when this modal closes.
const METRONOME_KEY = 'brassflows_scales_metronome_v1';
const METRONOME_DEFAULTS = { bpm: 120, countIn: 4, clickVolume: 50 };

const loadMetronome = () => {
  try {
    return { ...METRONOME_DEFAULTS, ...JSON.parse(localStorage.getItem(METRONOME_KEY)) };
  } catch (e) {
    return METRONOME_DEFAULTS;
  }
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getAvailableKeys = (instrument) => {
  if (instrument === 'trumpet' || instrument === 'frenchHorn') {
    return ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#', 'Ab', 'Eb', 'Bb', 'F'];
  }
  // trombone, euphonium, tuba
  return ['Bb', 'F', 'C', 'G', 'D', 'A', 'E', 'B', 'Gb', 'Db', 'Ab', 'Eb'];
};

const getDefaultKey = (instrument) =>
  instrument === 'trumpet' || instrument === 'frenchHorn' ? 'C' : 'Bb';

// Available octaves for a key + instrument, from scalesDict
const getOctaveRange = (instrument, key) => {
  const scales = scalesDict[instrument];
  if (!scales) return { min: 1, max: 6 };

  const octaves = [];
  for (let octave = 1; octave <= 6; octave++) {
    if (scales[`${key}${octave}`]) octaves.push(octave);
  }
  if (octaves.length === 0) return { min: 1, max: 6 };

  return { min: Math.min(...octaves), max: Math.max(...octaves) };
};

const getDefaultOctave = (instrument) =>
  getOctaveRange(instrument, getDefaultKey(instrument)).min;

const Options = ({ toggleOptions, page }) => {
  const initialOptions = useRef(loadFromLocalStorage()).current;

  const [selectedInstrument, setSelectedInstrument] = useState(initialOptions.selectedInstrument);
  const [selectedRange, setSelectedRange] = useState(initialOptions.selectedRange);
  const [slider1Value, setSlider1Value] = useState(initialOptions.slider1Value);
  const [slider2Value, setSlider2Value] = useState(initialOptions.slider2Value);
  const [keyLetter, setKeyLetter] = useState(initialOptions.keyLetter);
  const [accidental, setAccidental] = useState(initialOptions.accidental);
  const [mode, setMode] = useState(initialOptions.mode);
  const [volume, setVolume] = useState(initialOptions.volume);
  const [allowRareNotes, setAllowRareNotes] = useState(!!initialOptions.allowRareNotes);
  const [instrumentType, setInstrumentType] = useState(
    initialOptions.instrumentType || getDefaultType(initialOptions.selectedInstrument),
  );
  const [euphoniumTrigger, setEuphoniumTrigger] = useState(!!initialOptions.euphoniumTrigger);
  // Settings saved before notation existed keep their old reading (see resolveNotation)
  const [notation, setNotation] = useState(() => resolveNotation(initialOptions));
  const [errorMessage, setErrorMessage] = useState('');
  const [metronome, setMetronome] = useState(loadMetronome);

  const premium = usePremium();
  const sessionReady = useSession((s) => s.ready);

  // Scales specific
  const [scaleKey, setScaleKey] = useState(initialOptions.scaleKey ?? 'C');
  const [scaleOctave, setScaleOctave] = useState(initialOptions.scaleOctave ?? 4);

  // Track previous instrument so we only reset on a real change
  const prevInstrumentRef = useRef(initialOptions.selectedInstrument);

  // Always call the latest toggleOptions without re-binding listeners
  const toggleRef = useRef(toggleOptions);
  toggleRef.current = toggleOptions;

  // Close on Escape + lock page scroll while open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') toggleRef.current();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Free accounts can't keep Premium-only settings saved from before.
  // Waits for the session so a Premium user isn't reset while login is still loading.
  useEffect(() => {
    if (!sessionReady || premium) return;
    if (selectedRange === 'custom') setSelectedRange('advanced');
    if (keyLetter !== 'none') setKeyLetter('none');
  }, [sessionReady, premium, selectedRange, keyLetter]);

  // Only reset key/octave when the instrument actually changes
  useEffect(() => {
    if (prevInstrumentRef.current !== selectedInstrument) {
      setScaleKey(getDefaultKey(selectedInstrument));
      setScaleOctave(getDefaultOctave(selectedInstrument));
      prevInstrumentRef.current = selectedInstrument;
    }
  }, [selectedInstrument]);

  // Keep the octave inside the valid range for the chosen key
  useEffect(() => {
    const range = getOctaveRange(selectedInstrument, scaleKey);
    setScaleOctave((prev) => Math.min(Math.max(prev, range.min), range.max));
  }, [scaleKey, selectedInstrument]);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(
        'options',
        JSON.stringify({
          selectedInstrument,
          selectedRange,
          slider1Value,
          slider2Value,
          keyLetter,
          accidental,
          mode,
          volume,
          scaleKey,
          scaleOctave,
          allowRareNotes,
          instrumentType,
          notation,
          euphoniumTrigger,
        }),
      );
    } catch (e) {
      // ignore storage errors
    }
  }, [
    selectedInstrument,
    selectedRange,
    slider1Value,
    slider2Value,
    keyLetter,
    accidental,
    mode,
    volume,
    scaleKey,
    scaleOctave,
    allowRareNotes,
    instrumentType,
    notation,
    euphoniumTrigger,
  ]);

  const updateMetronome = (patch) => {
    setMetronome((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(METRONOME_KEY, JSON.stringify(next));
      } catch (e) {
        // ignore storage errors
      }
      return next;
    });
  };

  // Range presets
  useEffect(() => {
    const max = notesWithoutFlats[selectedInstrument].length - 1;
    if (selectedRange === 'beginner') {
      setSlider1Value(6);
      setSlider2Value(16);
    } else if (selectedRange === 'intermediate') {
      setSlider1Value(3);
      setSlider2Value(21);
    } else if (selectedRange === 'advanced') {
      setSlider1Value(0);
      setSlider2Value(max);
    } else {
      // Custom: keep sliders inside the new instrument's range
      setSlider1Value((v) => Math.min(v, max));
      setSlider2Value((v) => Math.min(v, max));
    }
  }, [selectedInstrument, selectedRange]);

  // Key signature validation
  useEffect(() => {
    let keySignature = keyLetter;
    if (keyLetter !== 'none') {
      if (accidental === 'sharp') keySignature += '#';
      else if (accidental === 'flat') keySignature += 'b';
      if (mode === 'minor') keySignature += 'm';
    }
    if (keySignature !== 'none' && !(keySignature in keySignatures)) {
      setErrorMessage('That key signature isn’t available');
    } else {
      setErrorMessage('');
    }
  }, [keyLetter, accidental, mode]);

  const currentOctaveRange = getOctaveRange(selectedInstrument, scaleKey);

  const incrementOctave = () =>
    setScaleOctave((prev) => Math.min(prev + 1, currentOctaveRange.max));
  const decrementOctave = () =>
    setScaleOctave((prev) => Math.max(prev - 1, currentOctaveRange.min));

  const maxIndex = notesWithoutFlats[selectedInstrument].length - 1;
  const isCustom = selectedRange === 'custom';

  // Scales never had the B♭-part reading, so it shows as Written there (the saved value is left alone)
  const shownNotation = page === 'scales' && notation === 'bbPart' ? 'written' : notation;
  const transposition = {
    instrument: selectedInstrument,
    trumpetKey: normalizeTrumpetKey(instrumentType),
    notation: shownNotation,
  };
  const showBbPart =
    page !== 'scales' && normalizeTrumpetKey(instrumentType) !== 'Bb';
  const notationChoices = showBbPart ? [...NOTATIONS, BB_PART_NOTATION] : NOTATIONS;
  const keyName = (key) => (selectedInstrument === 'trumpet' ? displayedTonic(key, transposition) : key);
  const shownOctave =
    selectedInstrument === 'trumpet'
      ? (parseNote(scaleToDisplayed([`${scaleKey}${scaleOctave}`], transposition)[0])?.octave ??
        scaleOctave)
      : scaleOctave;

  // Highlighted span of the dual-range track
  const lo = Math.min(slider1Value, slider2Value);
  const hi = Math.max(slider1Value, slider2Value);
  const fillStyle = {
    left: `${(lo / Math.max(maxIndex, 1)) * 100}%`,
    right: `${100 - (hi / Math.max(maxIndex, 1)) * 100}%`,
  };

  // Only a press that starts on the backdrop itself closes the panel
  const handleBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) toggleOptions();
  };

  const modal = (
    <div className={Styles.overlay} onMouseDown={handleBackdropMouseDown}>
      <div
        className={Styles.container}
        role="dialog"
        aria-modal="true"
        aria-label="Options"
      >
        <header className={Styles.header}>
          <h2>Options</h2>
          <button
            type="button"
            className={Styles.closeBtn}
            onClick={toggleOptions}
            aria-label="Close options"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </header>

        <div className={Styles.body}>
          {/* Instrument */}
          <section className={Styles.section}>
            <label className={Styles.label} htmlFor="instruments">
              Instrument
            </label>
            <select
              className={Styles.select}
              id="instruments"
              value={selectedInstrument}
              onChange={(e) => {
                setSelectedInstrument(e.target.value);
                setInstrumentType(getDefaultType(e.target.value));
              }}
            >
              {INSTRUMENTS.map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </select>
          </section>

          {/* Type of trumpet / tuba, 4th valve for euphonium (flashcards only) */}
          {(page !== 'scales' || selectedInstrument === 'trumpet') && getTypes(selectedInstrument) && (
            <section className={Styles.section}>
              <label className={Styles.label} htmlFor="instrumentType">
                Type
              </label>
              <select
                className={Styles.select}
                id="instrumentType"
                value={instrumentType}
                onChange={(e) => {
                  setInstrumentType(e.target.value);
                  // The B♭-part reading is the same thing as Written on a B♭ trumpet
                  if (e.target.value === 'Bb' && notation === 'bbPart') setNotation('written');
                }}
              >
                {getTypes(selectedInstrument).map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </section>
          )}

          {/* Written or concert notation (trumpet only) */}
          {selectedInstrument === 'trumpet' && (
            <section className={Styles.section}>
              <label className={Styles.label} htmlFor="notation">
                Notation
              </label>
              <select
                className={Styles.select}
                id="notation"
                value={shownNotation}
                onChange={(e) => setNotation(e.target.value)}
              >
                {notationChoices.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </section>
          )}

          {page !== 'scales' && selectedInstrument === 'euphonium' && (
            <section className={Styles.section}>
              <label className={Styles.label} htmlFor="euphoniumTrigger">
                <input
                  id="euphoniumTrigger"
                  type="checkbox"
                  checked={euphoniumTrigger}
                  onChange={(e) => setEuphoniumTrigger(e.target.checked)}
                />{' '}
                4th valve (trigger) on the F key
              </label>
            </section>
          )}

          {page === 'scales' ? (
            <>
              <section className={Styles.section}>
                <label className={Styles.label} htmlFor="scaleKey">
                  Key
                </label>
                <div className={Styles.row}>
                  <select
                    className={Styles.select}
                    id="scaleKey"
                    value={scaleKey}
                    onChange={(e) => setScaleKey(e.target.value)}
                  >
                    {getAvailableKeys(selectedInstrument).map((key) => (
                      <option key={key} value={key}>
                        {keyName(key)}
                      </option>
                    ))}
                  </select>
                  <span className={Styles.tag}>Major</span>
                </div>
              </section>

              <section className={Styles.section}>
                <span className={Styles.label}>Octave</span>
                <div className={Styles.stepper}>
                  <button
                    type="button"
                    className={Styles.stepBtn}
                    onClick={decrementOctave}
                    disabled={scaleOctave <= currentOctaveRange.min}
                    aria-label="Lower octave"
                    onKeyDown={(e) => {
                      if (e.key === ' ') e.preventDefault();
                    }}
                  >
                    −
                  </button>
                  <span className={Styles.stepValue}>{shownOctave}</span>
                  <button
                    type="button"
                    className={Styles.stepBtn}
                    onClick={incrementOctave}
                    disabled={scaleOctave >= currentOctaveRange.max}
                    aria-label="Raise octave"
                    onKeyDown={(e) => {
                      if (e.key === ' ') e.preventDefault();
                    }}
                  >
                    +
                  </button>
                </div>
              </section>

              <section className={Styles.section}>
                <div className={Styles.labelRow}>
                  <label className={Styles.label} htmlFor="tempo">
                    Metronome tempo
                  </label>
                  <span className={Styles.noteChip}>{metronome.bpm} BPM</span>
                </div>
                <div className={Styles.row}>
                  <button
                    type="button"
                    className={Styles.stepBtn}
                    onClick={() => updateMetronome({ bpm: clamp(metronome.bpm - 5, 30, 300) })}
                    aria-label="Slower"
                  >
                    −
                  </button>
                  <input
                    className={Styles.slider}
                    id="tempo"
                    type="range"
                    min="30"
                    max="300"
                    value={metronome.bpm}
                    style={{ '--fill': `${((metronome.bpm - 30) / 270) * 100}%` }}
                    onChange={(e) => updateMetronome({ bpm: Number(e.target.value) })}
                  />
                  <button
                    type="button"
                    className={Styles.stepBtn}
                    onClick={() => updateMetronome({ bpm: clamp(metronome.bpm + 5, 30, 300) })}
                    aria-label="Faster"
                  >
                    +
                  </button>
                </div>
              </section>

              <section className={Styles.section}>
                <span className={Styles.label}>Count-in beats</span>
                <div className={Styles.stepper}>
                  <button
                    type="button"
                    className={Styles.stepBtn}
                    onClick={() => updateMetronome({ countIn: clamp(metronome.countIn - 1, 0, 8) })}
                    disabled={metronome.countIn <= 0}
                    aria-label="Fewer count-in beats"
                    onKeyDown={(e) => {
                      if (e.key === ' ') e.preventDefault();
                    }}
                  >
                    −
                  </button>
                  <span className={Styles.stepValue}>{metronome.countIn}</span>
                  <button
                    type="button"
                    className={Styles.stepBtn}
                    onClick={() => updateMetronome({ countIn: clamp(metronome.countIn + 1, 0, 8) })}
                    disabled={metronome.countIn >= 8}
                    aria-label="More count-in beats"
                    onKeyDown={(e) => {
                      if (e.key === ' ') e.preventDefault();
                    }}
                  >
                    +
                  </button>
                </div>
              </section>

              <section className={Styles.section}>
                <div className={Styles.labelRow}>
                  <label className={Styles.label} htmlFor="clickVolume">
                    Click volume
                  </label>
                  <span className={Styles.noteChip}>{metronome.clickVolume}</span>
                </div>
                <input
                  className={Styles.slider}
                  id="clickVolume"
                  type="range"
                  min="0"
                  max="100"
                  value={metronome.clickVolume}
                  style={{ '--fill': `${metronome.clickVolume}%` }}
                  onChange={(e) => updateMetronome({ clickVolume: Number(e.target.value) })}
                />
              </section>
            </>
          ) : (
            <>
              {/* Range */}
              <section className={Styles.section}>
                <div className={Styles.labelRow}>
                  <span className={Styles.label}>Range</span>
                  {!premium && <span className={Styles.tag}>Custom is Premium</span>}
                </div>
                <div className={Styles.segmented} role="group" aria-label="Range">
                  {RANGES.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      className={`${Styles.segment} ${
                        selectedRange === r.value ? Styles.segmentActive : ''
                      }`}
                      onClick={() => {
                        if (r.value === 'custom' && !requirePremium(PREMIUM_REASON)) return;
                        setSelectedRange(r.value);
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>

                <div className={`${Styles.rangeBox} ${isCustom ? '' : Styles.rangeBoxDisabled}`}>
                  <div className={Styles.rangeReadout}>
                    <span className={Styles.noteChip}>
                      {writtenToDisplayed(notesWithoutFlats[selectedInstrument][slider1Value], transposition)}
                    </span>
                    <span className={Styles.rangeTo}>to</span>
                    <span className={Styles.noteChip}>
                      {writtenToDisplayed(notesWithoutFlats[selectedInstrument][slider2Value], transposition)}
                    </span>
                  </div>
                  <div className={Styles.sliderGroup}>
                    <div className={Styles.trackFill} style={fillStyle} />
                    <input
                      className={Styles.rangeInput}
                      id="slider1"
                      type="range"
                      min="0"
                      max={maxIndex}
                      value={slider1Value}
                      onChange={(e) => {
                        if (!requirePremium(PREMIUM_REASON)) return;
                        setSlider1Value(Number(e.target.value));
                      }}
                      disabled={!isCustom}
                      aria-label="Lowest note"
                    />
                    <input
                      className={Styles.rangeInput}
                      id="slider2"
                      type="range"
                      min="0"
                      max={maxIndex}
                      value={slider2Value}
                      onChange={(e) => {
                        if (!requirePremium(PREMIUM_REASON)) return;
                        setSlider2Value(Number(e.target.value));
                      }}
                      disabled={!isCustom}
                      aria-label="Highest note"
                    />
                  </div>
                </div>
              </section>

              {/* Rare spellings */}
              <section className={Styles.section}>
                <label className={Styles.label} htmlFor="rareNotes">
                  <input
                    id="rareNotes"
                    type="checkbox"
                    checked={allowRareNotes}
                    onChange={(e) => setAllowRareNotes(e.target.checked)}
                  />{' '}
                  Include C♭, F♭, B♯ and E♯
                </label>
              </section>

              {/* Key signature */}
              <section className={Styles.section}>
                <div className={Styles.labelRow}>
                  <label className={Styles.label} htmlFor="keyLetter">
                    Key signature
                  </label>
                  {!premium && <span className={Styles.tag}>Premium</span>}
                </div>
                <div className={Styles.row}>
                  <select
                    className={Styles.select}
                    id="keyLetter"
                    value={keyLetter}
                    onChange={(e) => {
                      if (e.target.value !== 'none' && !requirePremium(PREMIUM_REASON)) return;
                      setKeyLetter(e.target.value);
                    }}
                  >
                    <option value="none">None</option>
                    {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                  {keyLetter !== 'none' && (
                    <>
                      <select
                        className={`${Styles.select} ${Styles.selectSmall}`}
                        id="accidental"
                        value={accidental}
                        onChange={(e) => setAccidental(e.target.value)}
                        aria-label="Accidental"
                      >
                        <option value="natural">&#9838;</option>
                        <option value="sharp">#</option>
                        <option value="flat">b</option>
                      </select>
                      <select
                        className={Styles.select}
                        id="mode"
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                        aria-label="Mode"
                      >
                        <option value="major">Major</option>
                        <option value="minor">Minor</option>
                      </select>
                    </>
                  )}
                </div>
                {errorMessage && <p className={Styles.error}>{errorMessage}</p>}
              </section>
            </>
          )}

          {/* Volume */}
          <section className={Styles.section}>
            <div className={Styles.labelRow}>
              <label className={Styles.label} htmlFor="volume">
                Volume
              </label>
              <span className={Styles.noteChip}>{volume}</span>
            </div>
            <input
              className={Styles.slider}
              id="volume"
              type="range"
              min="0"
              max="100"
              value={volume}
              style={{ '--fill': `${volume}%` }}
              onChange={(e) => setVolume(Number(e.target.value))}
            />
          </section>
        </div>

        <footer className={Styles.footer}>
          <button type="button" className={Styles.doneBtn} onClick={toggleOptions}>
            Done
          </button>
        </footer>
      </div>
    </div>
  );

  // Render outside the page layout so parent overflow/stacking can't affect it
  return createPortal(modal, document.body);
};

export default Options;